import { useCallback, useMemo } from 'react';

import { useSaveCareerProfileFormMutation } from 'core';
import { evictVacancySkillMatch } from 'core/hooks/CareerCenter/evictVacancySkillMatch';
import { ICareerProfileFormValues } from 'organisms';
import { ICareerProfileData, mapProfileNode, transformProfileToInput } from './transform';

type SaveCareerProfile = [
  /** → `true`, только если сервер сохранил ВСЁ (одна транзакция, 0072). */
  (
    values: ICareerProfileFormValues,
    photo?: File | null,
    clearPhoto?: boolean,
    cvLanguages?: string[],
  ) => Promise<boolean>,
  {
    loading: boolean;
    success: boolean;
    errorCode?: string | null;
    errorField?: string | null;
    /** The freshly saved profile (fragment) mapped back to the view model. */
    savedProfile: ICareerProfileData | null;
  },
];

/**
 * Persists the whole profile (spec 0018, increment B).
 *
 * The mutation returns the FULL profile, so there is NO `refetchQueries`: the
 * caller resets the form baseline from `savedProfile` instead of a refetch that
 * would reinitialize Formik and wipe edits typed during the round-trip. The
 * returned CareerProfileType (normalised by id) also refreshes the
 * `myCareerProfile` cache, so the completeness bar / locales stay current.
 */
export const useSaveCareerProfile = (): SaveCareerProfile => {
  // ТЗ 0069: маршрут перезапрашиваем, а профиль — НЕТ. Сохранение двигает
  // этапы «Резюме» и «Портфолио», и их числа принадлежат серверу; сам же
  // `myCareerProfile` обновляется возвращённым объектом, и перезапрос
  // переинициализировал бы Formik, стерев правки, набранные за время
  // круговорота (об этом ниже).
  const [mutate, { data, loading }] = useSaveCareerProfileFormMutation({
    refetchQueries: ['CareerJourneyBoard'],
  });

  const save = useCallback(
    async (
      values: ICareerProfileFormValues,
      photo?: File | null,
      clearPhoto?: boolean,
      cvLanguages?: string[],
    ) => {
      try {
        const result = await mutate({
          variables: {
            input: transformProfileToInput(values, cvLanguages),
            // A File switches Apollo to multipart upload; `clearPhoto` removes an
            // existing avatar. Sending neither leaves the current photo untouched.
            photo: photo ?? undefined,
            clearPhoto: clearPhoto ? true : undefined,
          },
          // 0073: навыки профиля меняют соответствие каждой вакансии.
          update: (cache, { data: saved }) => {
            if (saved?.saveCareerProfile?.success) evictVacancySkillMatch(cache);
          },
        });
        return Boolean(result.data?.saveCareerProfile?.success);
      } catch {
        // Сеть или 500: черновик остаётся в форме, экран говорит «не сохранено».
        return false;
      }
    },
    [mutate],
  );

  const savedProfile = useMemo<ICareerProfileData | null>(() => {
    const node = data?.saveCareerProfile?.profile;
    return node ? mapProfileNode(node) : null;
  }, [data]);

  return [
    save,
    {
      loading,
      success: Boolean(data?.saveCareerProfile?.success),
      // Код, а не текст: серверное сообщение написано по-русски и
      // предназначено логам — на экран его класть нельзя.
      errorCode: data?.saveCareerProfile?.customErrors?.[0]?.code ?? null,
      errorField: data?.saveCareerProfile?.customErrors?.[0]?.field ?? null,
      savedProfile,
    },
  ];
};
